import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Info } from "lucide-react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { LabelWithTip } from "@/components/ui/tooltip";
import StatsExplainer from "@/components/StatsExplainer";
import HourlyTrendChart from "@/components/HourlyTrendChart";
import { formatPLN } from "@/lib/utils";
import type { ContentTimelineFetch, ContentTimelineGainReel, ContentTimelineHourlyPoint } from "@/lib/api";
import type { MetricTipId } from "@/lib/metricsGlossary";

type DaysOpt = 1 | 2 | 3 | 7;

const DAY_OPTIONS: { id: DaysOpt; label: string }[] = [
  { id: 1, label: "1 dzień" },
  { id: 2, label: "2 dni" },
  { id: 3, label: "3 dni" },
  { id: 7, label: "7 dni" },
];

const PLATFORM_LABEL: Record<string, string> = {
  instagram: "Instagram",
  tiktok: "TikTok",
  facebook: "Facebook",
  carousel: "Karuzela",
};

export default function ContentSalesTimeline() {
  const [days, setDays] = useState<DaysOpt>(7);

  const query = useQuery({
    queryKey: ["content-timeline", days],
    queryFn: () => api.siteAnalyticsContentTimeline(days),
    retry: false,
  });

  const data = query.data;
  const summary = data?.summary;
  const hours = data?.hourly?.hours ?? [];
  const publications = data?.publications ?? [];

  return (
    <div className="grid gap-5">
      <StatsExplainer variant="rolki" />

      {data?.fetch && <FetchStatusCard fetch={data.fetch} />}

      <div className="flex flex-wrap items-center gap-2">
        {DAY_OPTIONS.map((opt) => (
          <Button
            key={opt.id}
            size="sm"
            variant={opt.id === days ? "default" : "outline"}
            onClick={() => setDays(opt.id)}
          >
            {opt.label}
          </Button>
        ))}
        <span className="ml-2 text-xs text-muted-foreground">
          {data?.period?.label_pl ?? "czas PL"}
          {" · "}
          Godzina po godzinie
        </span>
      </div>

      {query.isLoading && (
        <Card>
          <CardContent className="py-8 text-sm text-muted-foreground">
            Wczytywanie linii czasu…
          </CardContent>
        </Card>
      )}
      {query.isError && (
        <Card>
          <CardContent className="py-8 text-sm text-muted-foreground">
            Nie udało się wczytać danych.
          </CardContent>
        </Card>
      )}

      {summary && (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Kpi tipId="hourly_chart" value={String(summary.publications)} label="Publikacje" />
            <Kpi
              tipId="reel_reach"
              value={summary.reach_total.toLocaleString("pl-PL")}
              hint={
                summary.reach_gained
                  ? `przyrost: ${summary.reach_gained.toLocaleString("pl-PL")} (IG ${
                      summary.reach_gained_ig?.toLocaleString("pl-PL") ?? 0
                    } / FB ${summary.reach_gained_fb?.toLocaleString("pl-PL") ?? 0} / karuzela ${
                      summary.reach_gained_carousel?.toLocaleString("pl-PL") ?? 0
                    })`
                  : summary.reach_missing
                    ? `${summary.reach_missing} bez liczby wyświetleń`
                    : "przyrost na wykresie po kolejnych fetchach"
              }
            />
            <Kpi tipId="manychat_keywords" value={String(summary.keyword_triggers)} />
            <Kpi
              tipId="kpi_today"
              value={formatPLN(summary.easycart_revenue ?? 0)}
              label="Przychód EC"
              hint={`${summary.easycart_paid_orders} zamówień`}
            />
          </div>

          <HourlyTrendChart
            title="Ruch i sprzedaż"
            subtitle="Każda linia to % własnego maksimum w oknie, żeby trend był czytelny."
            emptyNote="Publikacje i sprzedaż widać, gdy są w wybranym oknie."
            data={hours.map((h, i) => ({
              hour: i,
              label: h.label,
              published_count: h.published_count ?? 0,
              site_sessions: h.site_sessions ?? 0,
              cart_sessions: h.cart_sessions ?? 0,
              easycart_paid_orders: h.easycart_paid_orders ?? 0,
              top_via: h.top_via ?? [],
              published_in_hour: h.published_in_hour ?? [],
            }))}
            mode="line"
            scale="percent"
            richTooltip="traffic-sales"
            xInterval={days === 1 ? 2 : days === 7 ? 11 : 5}
            series={[
              { key: "published_count", label: "Publikacje", color: "#6366f1" },
              { key: "site_sessions", label: "Wejścia", color: "#0ea5e9" },
              { key: "cart_sessions", label: "Koszyki", color: "#f59e0b" },
              { key: "easycart_paid_orders", label: "Zakupy EC", color: "#22c55e" },
            ]}
          />

          <HourlyTrendChart
            title="Przyrost wyświetleń IG, FB i karuzel"
            subtitle="Wysokość to suma. Kolory w środku to składowe (IG, FB, karuzela)."
            emptyNote="Przyrost pojawi się po drugim odczycie (rolki :20, karuzele z insights)."
            data={hours.map((h, i) => ({
              hour: i,
              label: h.label,
              reach_gained_ig: h.reach_gained_ig ?? 0,
              reach_gained_fb: h.reach_gained_fb ?? 0,
              reach_gained_carousel: h.reach_gained_carousel ?? 0,
            }))}
            mode="area"
            scale="absolute"
            xInterval={days === 1 ? 2 : days === 7 ? 11 : 5}
            series={[
              { key: "reach_gained_ig", label: "Instagram", color: "#e1306c", stackId: "reach" },
              { key: "reach_gained_fb", label: "Facebook", color: "#1877f2", stackId: "reach" },
              { key: "reach_gained_carousel", label: "Karuzela", color: "#eab308", stackId: "reach" },
            ]}
          />

          <GainHoursList hours={data?.hourly?.gain_hours ?? []} />

          {data?.notes_pl && data.notes_pl.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Jak to liczymy</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm leading-relaxed text-muted-foreground">
                  {data.notes_pl.map((line, i) => (
                    <li key={i} className="flex gap-2">
                      <Info className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Publikacje w oknie</CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              {publications.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Brak publikacji (rolki i karuzele) w tym zakresie.
                </p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                      <th className="py-2 pr-3">Godzina</th>
                      <th className="py-2 pr-3">Platforma</th>
                      <th className="py-2 pr-3">Treść</th>
                      <th className="py-2 pr-3 text-right">Zasięg</th>
                      <th className="py-2 text-right">Hasła</th>
                    </tr>
                  </thead>
                  <tbody>
                    {publications.map((p) => (
                      <tr key={`${p.platform}-${p.url}-${p.published_at}`} className="border-b border-border/50">
                        <td className="py-2 pr-3 tabular-nums">
                          {formatPubTime(p.published_at)}
                          {p.time_precision === "day" && (
                            <Badge variant="outline" className="ml-2 text-[10px]">
                              tylko dzień
                            </Badge>
                          )}
                        </td>
                        <td className="py-2 pr-3">{PLATFORM_LABEL[p.platform] ?? p.platform}</td>
                        <td className="py-2 pr-3">
                          {p.url ? (
                            <a
                              href={p.url}
                              target="_blank"
                              rel="noreferrer"
                              className="text-brand hover:underline"
                            >
                              {p.title || p.url}
                            </a>
                          ) : (
                            p.title || "-"
                          )}
                        </td>
                        <td className="py-2 pr-3 text-right tabular-nums">
                          {p.view_count == null ? "-" : p.view_count.toLocaleString("pl-PL")}
                        </td>
                        <td className="py-2 text-right tabular-nums">{p.keywords_total}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

function GainHoursList({ hours }: { hours: ContentTimelineHourlyPoint[] }) {
  if (hours.length === 0) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Top 3 w godzinach przyrostu</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Brak godzin z przyrostem views. Lista pojawi się po drugim fetchu Graph.
          </p>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Top 3 w godzinach przyrostu</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-4">
        {hours.map((h) => (
          <div key={h.hour_key} className="rounded-md border border-border/60 p-3">
            <div className="mb-2 grid gap-0.5 text-sm">
              <span className="font-medium">{h.label}</span>
              <span className="tabular-nums text-muted-foreground">
                IG +{(h.reach_gained_ig ?? 0).toLocaleString("pl-PL")}
              </span>
              <span className="tabular-nums text-muted-foreground">
                FB +{(h.reach_gained_fb ?? 0).toLocaleString("pl-PL")}
              </span>
              <span className="tabular-nums text-muted-foreground">
                Karuzela +{(h.reach_gained_carousel ?? 0).toLocaleString("pl-PL")}
              </span>
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <TopGainList title="Instagram" items={h.top_instagram ?? []} />
              <TopGainList title="Facebook" items={h.top_facebook ?? []} />
              <TopGainList title="Karuzela" items={h.top_carousel ?? []} />
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function topGainLabel(item: ContentTimelineGainReel): string {
  const title = (item.title || "").trim();
  if (title) return title;
  const url = (item.url || "").trim();
  const ig = url.match(/instagram\.com\/(?:reel|p)\/([^/?#]+)/i);
  if (ig?.[1]) return `Rolka IG · ${ig[1]}`;
  const fb = url.match(/facebook\.com\/(?:reel|videos)\/(\d+)/i);
  if (fb?.[1]) return `Rolka FB · ${fb[1]}`;
  if (item.platform === "carousel" && item.reel_key) return `Karuzela · ${item.reel_key.slice(0, 8)}`;
  return item.reel_key || url || "publikacja";
}

function TopGainList({ title, items }: { title: string; items: ContentTimelineGainReel[] }) {
  return (
    <div>
      <div className="mb-1 text-xs uppercase tracking-wide text-muted-foreground">{title}</div>
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground">Brak przyrostu.</p>
      ) : (
        <ol className="space-y-1 text-sm">
          {items.map((item, i) => {
            const label = topGainLabel(item);
            const href =
              item.url && /^https?:\/\//i.test(item.url.trim()) ? item.url.trim() : "";
            const body = (
              <>
                <span className="tabular-nums text-muted-foreground">{i + 1}.</span>{" "}
                <span className="break-all">{label}</span>
                <span className="ml-2 tabular-nums text-muted-foreground">
                  +{(item.gain ?? 0).toLocaleString("pl-PL")}
                </span>
              </>
            );
            return (
              <li key={`${item.platform}-${item.reel_key}-${i}`}>
                {href ? (
                  <a href={href} target="_blank" rel="noreferrer" className="text-brand hover:underline">
                    {body}
                  </a>
                ) : (
                  body
                )}
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

const FETCH_STATUS_PL: Record<string, string> = {
  success: "OK",
  partial: "częściowo",
  error: "błąd",
  skipped: "pominięte",
  empty: "brak runu",
};

const PLATFORM_ORDER = ["instagram", "tiktok", "facebook"] as const;

function FetchStatusCard({ fetch }: { fetch: ContentTimelineFetch }) {
  const steps = fetch.platforms ?? [];
  const dumps = fetch.dumps ?? {};
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Ostatnie pobranie rolek</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-3 text-sm">
        <div className="flex flex-wrap items-center gap-2 text-muted-foreground">
          {fetch.last_run_at_pl ? (
            <span>
              Run: <span className="font-medium text-foreground">{fetch.last_run_at_pl}</span>
              {fetch.trigger ? ` (${fetch.trigger})` : ""}
            </span>
          ) : (
            <span>Jeszcze nie było fetcha na tym serwerze.</span>
          )}
          {fetch.status && (
            <Badge variant={fetch.status === "success" ? "default" : "outline"}>
              {FETCH_STATUS_PL[fetch.status] ?? fetch.status}
            </Badge>
          )}
          {fetch.days ? <span>okno {fetch.days} dni</span> : null}
          {fetch.since && <span>od {fetch.since}</span>}
        </div>
        <ul className="grid gap-2 sm:grid-cols-3">
          {PLATFORM_ORDER.map((key) => {
            const step = steps.find((s) => s.platform === key);
            const dump = dumps[key];
            return (
              <li key={key} className="rounded-md border border-border/60 p-3">
                <div className="font-medium">{PLATFORM_LABEL[key]}</div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {step ? (
                    <>
                      {step.backend ?? "?"} · {FETCH_STATUS_PL[step.status || ""] ?? step.status}
                      {step.media != null && step.media > 0 ? ` · ${step.media} mediów` : ""}
                      {step.comments != null && step.comments > 0
                        ? ` · ${step.comments} komentarzy`
                        : ""}
                    </>
                  ) : (
                    "brak wyniku runu"
                  )}
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  Dump: {dump?.items ?? 0} wpisów
                  {dump?.last_updated_pl ? `, aktualizacja ${dump.last_updated_pl}` : ""}
                </div>
                {step?.error && (
                  <div className="mt-1 text-xs text-destructive">{step.error}</div>
                )}
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}

function formatPubTime(iso: string): string {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("pl-PL", {
    timeZone: "Europe/Warsaw",
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function Kpi({
  tipId,
  value,
  label,
  hint,
}: {
  tipId: MetricTipId;
  value: string;
  label?: string;
  hint?: string;
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-xs text-muted-foreground">
          <LabelWithTip tipId={tipId} label={label} />
        </div>
        <div className="mt-1 text-2xl font-bold tabular-nums">{value}</div>
        {hint && <div className="mt-1 text-[11px] text-muted-foreground">{hint}</div>}
      </CardContent>
    </Card>
  );
}
